import React from "react";
import { Modal, Accordion } from "react-bootstrap";
import { FaBuilding, FaEnvelope, FaUsers, FaUser } from "react-icons/fa";
import styles from "./style.module.scss";
import { resolveLogoUrl } from "../../utils";

interface APIStructure {
  organizationId: number;
  organizationName: string;
  type: string;
  organizationEmail: string;
  logo: string;
  organizationLogo: string;
  auditeeMembers: { name: string; email: string }[];
  applications: { applicationId: number; applicationName: string }[];
  auditeeLead: any[];
}

interface PopupModalProps {
  show: boolean;
  onClose: () => void;
  title: string;
  auditCode?: string;
  organizations?: APIStructure[];
}

const OrganizationModal: React.FC<PopupModalProps> = ({
  show,
  onClose,
  title,
  auditCode,
  organizations = [],
}) => {
  const mappedOrganizations = organizations.map((org) => ({
    name: org.organizationName,
    sector: org.type || "N/A",
    logo: org.organizationLogo,
    application:
      org.applications?.map((a) => a.applicationName).join(", ") || "N/A",
    email: org.organizationEmail || "—",
    team: {
      lead: org.auditeeLead?.[0]?.name || "—",
      members: org.auditeeMembers?.map((m) => m.name) || [],
    },
  }));

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size="lg"
      backdrop="static"
      className={styles.orgModal}
      enforceFocus={false}
    >
      <Modal.Body className={styles.modalBody}>
        {/* Header */}
        <div className={styles.header}>
          <div>
            <span className={styles.title}>
              {title}
              <span className={styles.code}>{" "}{auditCode}</span>
            </span>
          </div>
          <button onClick={onClose} className={styles.closeBtn}>×</button>
        </div>

        {/* Accordion (even if only one org, for consistent layout) */}
        <Accordion alwaysOpen defaultActiveKey={["0"]}>
          {mappedOrganizations.map((org, index) => (
            <Accordion.Item
              eventKey={index.toString()}
              key={index}
              className={styles.accordionItem}
            >
              <Accordion.Header>
                <div className={styles.orgHeader}>
                  {org.logo ? (
                    <img src={resolveLogoUrl(org.logo) ?? undefined} className={styles.orgIcon} alt="org-logo" />
                  ) : (
                    <FaBuilding className={styles.orgIcon} />
                  )}
                  {org.name}
                </div>
              </Accordion.Header>

              <Accordion.Body>
                <div className={styles.card}>
                  {/* Organization info */}
                  <div className={styles.infoRow}>
                    <div className={styles.infoItem}>
                      <FaBuilding className={styles.icon} />
                      <div>
                        <div className={styles.label}>Industry/ Sector</div>
                        <div className={styles.value}>{org.sector}</div>
                      </div>
                    </div>
                    <div className={styles.infoItem}>
                      <FaBuilding className={styles.icon} />
                      <div>
                        <div className={styles.label}>Application</div>
                        <div className={styles.value}>{org.application}</div>
                      </div>
                    </div>
                    <div className={styles.infoItem}>
                      <FaEnvelope className={styles.icon} />
                      <div>
                        <div className={styles.label}>Organization Email</div>
                        <div className={styles.value}>{org.email}</div>
                      </div>
                    </div>
                  </div>

                  {/* Team Lead */}
                  <div className={styles.teamSection}>
                    <div className={styles.teamTitle}>
                      <FaUser className={styles.icon} /> Auditee Team Lead
                    </div>
                    <div className={styles.teamBox}>{org.team.lead}</div>
                  </div>

                  {/* Team Members */}
                  <div className={styles.teamSection}>
                    <div className={styles.teamTitle}>
                      <FaUsers className={styles.icon} /> Auditee Team Members
                    </div>
                    <div className={styles.teamBox}>
                      {org.team.members.length > 0
                        ? org.team.members.join(", ")
                        : "No members listed"}
                    </div>
                  </div>
                </div>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      </Modal.Body>
    </Modal>
  );
};

export default OrganizationModal;
